import { useState, useContext, useEffect, useMemo } from "react";
import { ColumnFiltersState } from "@tanstack/react-table";
import { Plus, CreditCard, DollarSign, MailPlus, Receipt } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Table,
  TableBody,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@pushpress/pushpress-ui";
import { useGetUserPaymentMethods } from "../../../../../apis/billingApi.hooks";
import { useUserContext } from "../../../hooks/useUserContext";
import SectionHeader from "../../shared/SectionHeader";
import SectionTitle from "../../shared/SectionTitle";
import PaymentMethodCard from "./PaymentMethodCard";
import AddPaymentMethod from "./AddPaymentMethod/AddPaymentMethod";
import ErrorState from "../../shared/ErrorState";
import TableSkeleton from "../../shared/TableSkeleton";
import EmptyTable from "../../shared/EmptyTable";
import { AppContext } from "../../../../../context/app";
import ConnectStripeInfoModal from "./AddPaymentMethod/ConnectStripeInfoModal";
import BillingHistoryTable from "./BillingHistoryTable";
import { isPaymentMethodExpired } from "../../../helpers";

const defaultActiveFilter = [
  {
    id: "type",
    value: ["invoice"],
  },
];

function AddPaymentMethodsButton({
  isStripeConnected,
  isBankConnected,
  handleAddPaymentMethod,
  setIsConnectStripeInfoModalOpen,
}: {
  isStripeConnected: boolean;
  isBankConnected: boolean;
  handleAddPaymentMethod: (requestType: RequestType) => void;
  setIsConnectStripeInfoModalOpen: (isOpen: boolean) => void;
}) {
  if (isStripeConnected) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm">
            <Plus className="tw-mr-1 tw-h-4 tw-w-4" />
            Add Payment Method
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            data-testid="add-payment-method-credit-card"
            onClick={() => handleAddPaymentMethod("card")}
          >
            Credit Card
          </DropdownMenuItem>
          {isBankConnected && (
            <DropdownMenuItem
              data-testid="add-payment-method-bank-account"
              onClick={() => handleAddPaymentMethod("bank")}
            >
              Bank Account
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
  return (
    <Button onClick={() => setIsConnectStripeInfoModalOpen(true)}>
      <Plus className="tw-mr-1 tw-h-4 tw-w-4" />
      Add Payment Method
    </Button>
  );
}

export type RequestType = "bank" | "card";
export type ViewMode = "invoices" | "pending" | "payments";

export default function Billing({ userUuid }: { userUuid: string }) {
  const { client } = useContext(AppContext);
  const { setHasExpiredPaymentMethods } = useUserContext();
  const [isAddPaymentModalOpen, setIsAddPaymentModalOpen] = useState(false);
  const [isConnectStripeInfoModalOpen, setIsConnectStripeInfoModalOpen] =
    useState(false);
  const [selectedRequestType, setSelectedRequestType] =
    useState<RequestType>("card");

  const [viewMode, setViewMode] = useState<ViewMode>("invoices");
  const [columnFilters, setColumnFilters] =
    useState<ColumnFiltersState>(defaultActiveFilter);

  const {
    data: billingData,
    isFetching,
    error,
    refetch,
  } = useGetUserPaymentMethods(userUuid);

  const handleAddPaymentMethod = (requestType: RequestType) => {
    setSelectedRequestType(requestType);
    setIsAddPaymentModalOpen(true);
  };

  const isStripeConnected = !!client.stripeUserId;
  const isBankConnected = !!client.bankConnected;

  const hasExpiredPaymentMethods = useMemo(() => {
    if (!billingData || billingData.length === 0) {
      return false;
    }
    return billingData.some((paymentMethod) =>
      isPaymentMethodExpired(
        paymentMethod.billingExpMonth,
        paymentMethod.billingExpYear,
      ),
    );
  }, [billingData]);

  useEffect(() => {
    setHasExpiredPaymentMethods(hasExpiredPaymentMethods);
  }, [hasExpiredPaymentMethods, setHasExpiredPaymentMethods]);

  const setFilters = (status: ViewMode) => {
    switch (status) {
      case "pending":
        setColumnFilters([
          {
            id: "type",
            value: ["invoice"],
          },
          {
            id: "isPending",
            value: [true],
          },
        ]);
        break;
      case "invoices":
        setColumnFilters([
          {
            id: "type",
            value: ["invoice"],
          },
        ]);
        break;
      case "payments":
        setColumnFilters([
          {
            id: "type",
            value: ["payment"],
          },
        ]);
        break;
      default:
        setColumnFilters(defaultActiveFilter);
        break;
    }
    setViewMode(status);
  };

  return (
    <div className="tw-space-y-4">
      <SectionHeader
        title="Billing"
        actions={
          <div className="tw-flex tw-items-center tw-gap-2">
            <Button variant="outline" size="sm">
              <MailPlus className="tw-mr-1 tw-h-4 tw-w-4" />
              Request Payment Method
            </Button>
            <Button
              onClick={() => {
                window.open(
                  `/invoices/create/${userUuid}`,
                  "_blank",
                  "noopener,noreferrer",
                );
              }}
              size="sm"
              variant="outline"
            >
              <DollarSign className="tw-mr-1 tw-h-4 tw-w-4" />
              Create a Charge
            </Button>
            <AddPaymentMethodsButton
              isStripeConnected={isStripeConnected}
              isBankConnected={isBankConnected}
              handleAddPaymentMethod={handleAddPaymentMethod}
              setIsConnectStripeInfoModalOpen={setIsConnectStripeInfoModalOpen}
            />
          </div>
        }
      />
      <div className="tw-rounded-lg tw-border tw-p-4 tw-bg-white tw-flex tw-flex-col tw-gap-4">
        <SectionTitle icon={CreditCard} title="Payment Methods" />
        <div className="tw-w-full">
          {error ? (
            <ErrorState refetch={refetch} />
          ) : isFetching ? (
            <TableSkeleton numCols={2} numRows={5} />
          ) : !billingData || billingData.length === 0 ? (
            <Table>
              <TableBody>
                <EmptyTable
                  userUuid={userUuid}
                  title="No payment methods for this user"
                  subtitle="Add a payment method to get started."
                  action={
                    <AddPaymentMethodsButton
                      isStripeConnected={isStripeConnected}
                      isBankConnected={isBankConnected}
                      handleAddPaymentMethod={handleAddPaymentMethod}
                      setIsConnectStripeInfoModalOpen={
                        setIsConnectStripeInfoModalOpen
                      }
                    />
                  }
                />
              </TableBody>
            </Table>
          ) : (
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-4">
              {billingData
                ?.sort((a, b) => b.isPrimary - a.isPrimary)
                .map((paymentMethod) => (
                  <PaymentMethodCard
                    userUuid={userUuid}
                    key={paymentMethod.id}
                    method={paymentMethod}
                  />
                ))}
            </div>
          )}
        </div>
      </div>
      <ConnectStripeInfoModal
        isOpen={isConnectStripeInfoModalOpen}
        closeModal={() => setIsConnectStripeInfoModalOpen(false)}
      />
      <Dialog
        open={isAddPaymentModalOpen}
        onOpenChange={() => setIsAddPaymentModalOpen(false)}
        modal
      >
        <DialogContent
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="tw-text-neutral-800 tw-text-center">
              Add payment method
            </DialogTitle>
          </DialogHeader>
          <AddPaymentMethod
            userUuid={userUuid}
            requestType={selectedRequestType}
            closeAddPaymentModal={() => setIsAddPaymentModalOpen(false)}
          />
        </DialogContent>
      </Dialog>
      <div className="tw-rounded-lg tw-border tw-p-4 tw-bg-white tw-flex tw-flex-col tw-gap-4">
        <SectionTitle icon={Receipt} title="Billing History" />
        <div className="tw-flex tw-flex-col tw-gap-4">
          <Tabs
            value={viewMode}
            onValueChange={(value: ViewMode) => setFilters(value)}
          >
            <TabsList className="tw-h-9">
              <TabsTrigger value="invoices" className="tw-text-xs">
                Invoices
              </TabsTrigger>
              <TabsTrigger
                value="pending"
                className="tw-text-xs"
                data-testid="pending-tab"
              >
                Pending
              </TabsTrigger>
              <TabsTrigger
                value="payments"
                className="tw-text-xs"
                data-testid="payments-tab"
              >
                Payments
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <BillingHistoryTable
            viewMode={viewMode}
            userUuid={userUuid}
            columnFilters={columnFilters}
          />
        </div>
      </div>
    </div>
  );
}
